import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, map, take } from 'rxjs';

import {
  selectMePermissions,
  selectMePermissionsError,
} from '../Store/rbac-sod/rbac-sod.selectors';
import { PermissionGateService } from './permission-gate.service';

/**
 * Route guard for Spend Reclassification.
 *
 * The route had NO guard at all: anyone who knew the URL could open the page,
 * and both chips rendered for everyone. The controller is now gated per
 * endpoint, so the data was never actually exposed — but an ungated route
 * still lands the user on a page whose every request comes back denied, which
 * reads as broken rather than as forbidden.
 *
 * `reclassification.view` is the entry key: edit and approve are each granted
 * alongside it, so holding either implies it.
 *
 * The wait matters. `has$` default-denies until the permissions slice
 * hydrates, so deciding immediately would bounce legitimate users on every
 * cold navigation. This waits for the slice to settle either way, and a load
 * failure stays fail-closed — no permission proven, no page.
 *
 * Denies land on `/dashboard`, the app's landing route.
 */
export const reclassificationGuard: CanActivateFn = () => {
  const permissionGate = inject(PermissionGateService);
  const store = inject(Store);
  const router = inject(Router);

  return combineLatest([
    permissionGate.has$('reclassification.view'),
    store.select(selectMePermissions),
    store.select(selectMePermissionsError),
  ]).pipe(
    filter(([, me, error]) => me != null || error != null),
    take(1),
    map(([allowed]) => (allowed ? true : router.parseUrl('dashboard'))),
  );
};
